import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CodeMirror from "@uiw/react-codemirror";
import { sql, PostgreSQL } from "@codemirror/lang-sql";
import { PageLayout } from "../components/PageLayout.js";
import { TanTableViz } from "../components/TanTableViz.js";
import type { QueryResultsPayload } from "../components/QueryResultsGrid.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { fetchExecutionLog, type ExecutionLogEntry } from "../lib/executionLog.js";
import { queryResultsToTanTableModel } from "../lib/queryResultsToTanTable.js";
import { useToast } from "../components/Toast.js";
import { ColumnDomainSelect, type ColumnDomain } from "../components/ColumnDomainControl.js";
import { nextOverrideMap, typeValidationMessage, type TypeValidationFailures } from "../lib/columnDomain.js";

const sqlExtensions = [sql({ dialect: PostgreSQL })];

type Connector = {
  id: string;
  name: string;
};

type DataProvider = {
  id: string;
  name: string;
  description: string | null;
  /** ADR-0085 Phase 2 — defaults to 'connector_sql' on legacy rows. */
  provider_kind?: "connector_sql" | "plex_api";
  connector_id: string | null;
  connector_name: string | null;
  integration_id?: string | null;
  source_spec:
    | { specVersion: "source.v1"; kind: "sql"; sql: string }
    | { datasource_id: number };
  parameters: Array<{
    name: string;
    type: "string" | "int" | "decimal" | "bool" | "date" | "datetime";
    required: boolean;
    default?: string | number | boolean | null;
    description?: string | null;
  }>;
  last_source_result: QueryResultsPayload | null;
  last_run_at: string | null;
  last_duration_ms: number | null;
  last_row_count: number | null;
  source_sql_locked?: boolean;
  /** ADR-0146: schema lock — freezes the output shape, orthogonal to the SQL lock. */
  schema_locked?: boolean;
  schema_locked_at?: string | null;
  /** ADR-0147: per-column Atlas-domain overrides ({ column → domain }). */
  column_type_overrides?: Record<string, string>;
};

export function DataProviderEditor() {
  const params = useParams<{ id: string }>();
  const providerId = params.id ?? "";
  const nav = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [connectorId, setConnectorId] = useState("");
  const [sql, setSql] = useState("");
  const [parameters, setParameters] = useState<DataProvider["parameters"]>([]);
  const [savedSnapshot, setSavedSnapshot] = useState("");
  const [activeTab, setActiveTab] = useState<"metadata" | "parameters" | "source" | "schema" | "history">("metadata");
  const [cache, setCache] = useState<{
    sourceResult: QueryResultsPayload | null;
    runAt: string | null;
    durationMs: number | null;
    rowCount: number | null;
  } | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [executions, setExecutions] = useState<ExecutionLogEntry[]>([]);
  const [selectedExecution, setSelectedExecution] = useState<ExecutionLogEntry | null>(null);
  const [sourceSqlLocked, setSourceSqlLocked] = useState(false);
  // ADR-0146: schema lock is independent of the SQL lock — SQL edits stay
  // allowed on a schema-locked provider; the freeze flags the consequence.
  const [schemaLocked, setSchemaLocked] = useState(false);
  const [schemaLockedAt, setSchemaLockedAt] = useState<string | null>(null);
  // ADR-0147: per-column Domain overrides — autosaved on change (debounced
  // PATCH); the response's re-typed cache re-renders the Schema Contract. A
  // change that doesn't fit the data is rejected + reverted (validate-first).
  const [columnOverrides, setColumnOverrides] = useState<Record<string, string>>({});
  const overridesPatchTimer = useRef<number | null>(null);

  const cacheTanModel = useMemo(
    () => (cache?.sourceResult ? queryResultsToTanTableModel(cache.sourceResult) : null),
    [cache?.sourceResult]
  );

  const dirty =
    savedSnapshot !==
    JSON.stringify({
      name: name.trim(),
      description: description.trim() || null,
      sql: sql.trim(),
      connector_id: connectorId || null,
      parameters
    });

  const savedSqlFromSnapshot = useMemo(() => {
    try {
      const o = JSON.parse(savedSnapshot) as { sql?: string };
      return (o.sql ?? "").trim();
    } catch {
      return "";
    }
  }, [savedSnapshot]);
  const sqlDirty = sql.trim() !== savedSqlFromSnapshot;

  async function loadExecutionHistory() {
    try {
      const entries = await fetchExecutionLog({
        data_provider_id: providerId,
        limit: 20
      });
      setExecutions(entries);
    } catch {
      setExecutions([]);
    }
  }

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [connectorsData, providerData, executionEntries] = await Promise.all([
        apiFetch<{ connectors: Connector[] }>("/api/tenant/connectors"),
        apiFetch<{ data_provider: DataProvider }>(`/api/tenant/data-providers/${encodeURIComponent(providerId)}`),
        fetchExecutionLog({ data_provider_id: providerId, limit: 20 })
      ]);
      setConnectors(connectorsData.connectors);
      setExecutions(executionEntries);
      const provider = providerData.data_provider;
      setName(provider.name);
      setDescription(provider.description ?? "");
      // ADR-0085 Phase 2 (revisited 2026-05-26): Plex API providers don't
      // share — the create surface rejects them. Any plex_api row reaching
      // this editor is legacy from an earlier prototype build; refuse cleanly.
      if ((provider.provider_kind ?? "connector_sql") === "plex_api") {
        setError(
          "Plex API providers are no longer shared. " +
          "Configure Plex calls per node on the operation canvas; delete this row when convenient."
        );
        setLoading(false);
        return;
      }
      const sourceSql = "sql" in provider.source_spec ? provider.source_spec.sql : "";
      setConnectorId(provider.connector_id ?? "");
      setSql(sourceSql ?? "");
      setSourceSqlLocked(Boolean(provider.source_sql_locked));
      setSchemaLocked(Boolean(provider.schema_locked));
      setSchemaLockedAt(provider.schema_locked_at ?? null);
      setParameters(provider.parameters ?? []);
      setSavedSnapshot(
        JSON.stringify({
          name: provider.name,
          description: provider.description ?? null,
          sql: sourceSql ?? "",
          connector_id: provider.connector_id ?? null,
          parameters: provider.parameters ?? []
        })
      );
      setColumnOverrides(provider.column_type_overrides ?? {});
      if (provider.last_source_result && provider.last_run_at) {
        setCache({
          sourceResult: provider.last_source_result,
          runAt: provider.last_run_at,
          durationMs: provider.last_duration_ms ?? null,
          rowCount: provider.last_row_count ?? null
        });
      } else {
        setCache(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  /** ADR-0147 (validate-before-convert): Domain change → optimistic state,
   *  debounced PATCH. On success the response carries the re-typed cache
   *  (re-rendered immediately, no re-pull). If a value doesn't fit the target,
   *  the hub rejects with 422 `type_validation_failed`: toast which values
   *  block it and revert the control to its prior state. */
  function changeColumnDomain(columnName: string, domain: ColumnDomain | null) {
    const prior = columnOverrides;
    const next = nextOverrideMap(prior, columnName, domain);
    setColumnOverrides(next);
    if (overridesPatchTimer.current !== null) window.clearTimeout(overridesPatchTimer.current);
    overridesPatchTimer.current = window.setTimeout(() => {
      overridesPatchTimer.current = null;
      void (async () => {
        try {
          const res = await apiFetch<{ data_provider: DataProvider }>(
            `/api/tenant/data-providers/${encodeURIComponent(providerId)}`,
            { method: "PATCH", body: { column_type_overrides: next } }
          );
          const p = res.data_provider;
          setColumnOverrides(p.column_type_overrides ?? next);
          if (p.last_source_result && p.last_run_at) {
            setCache({
              sourceResult: p.last_source_result,
              runAt: p.last_run_at,
              durationMs: p.last_duration_ms ?? null,
              rowCount: p.last_row_count ?? null
            });
          }
        } catch (e) {
          setColumnOverrides(prior); // revert the control
          if (e instanceof ApiRequestError && e.code === "type_validation_failed" && domain) {
            const failures = (e.data?.failures ?? {}) as TypeValidationFailures;
            toast.push({ kind: "error", message: typeValidationMessage(failures, domain) });
          } else {
            toast.push({
              kind: "error",
              message: e instanceof Error ? e.message : "Could not save the column type change."
            });
          }
        }
      })();
    }, 400);
  }

  useEffect(() => {
    if (!providerId) return;
    void load();
  }, [providerId]);

  // ADR-0147: drop any pending Domain-override PATCH when this editor unmounts.
  // Without this the debounced timer survives navigation and fires against a dead
  // component — and because browsers heavily throttle timers in a hidden tab, it
  // can land far later than the 400ms debounce implies, writing a stale override
  // map (e.g. an empty one) over whatever the provider holds by then.
  useEffect(() => {
    return () => {
      if (overridesPatchTimer.current !== null) {
        window.clearTimeout(overridesPatchTimer.current);
        overridesPatchTimer.current = null;
      }
    };
  }, []);

  const canSave = name.trim() !== "" && sql.trim() !== "" && connectorId !== "";

  async function save(opts?: { quiet?: boolean }): Promise<boolean> {
    if (!name.trim() || !sql.trim()) {
      if (!opts?.quiet) toast.push({ kind: "error", message: "Name and SQL are required" });
      return false;
    }
    if (!connectorId) {
      if (!opts?.quiet) toast.push({ kind: "error", message: "Connector is required" });
      return false;
    }
    setBusy(true);
    try {
      const body: Record<string, unknown> = {
        name: name.trim(),
        description: description.trim() || null,
        connector_id: connectorId,
        parameters
      };
      if (!sourceSqlLocked) {
        body.source_spec = { specVersion: "source.v1", kind: "sql", sql: sql.trim() };
      }
      const res = await apiFetch<{ data_provider: DataProvider }>(
        `/api/tenant/data-providers/${encodeURIComponent(providerId)}`,
        {
          method: "PATCH",
          body
        }
      );
      setSourceSqlLocked(Boolean(res.data_provider.source_sql_locked));
      setSchemaLocked(Boolean(res.data_provider.schema_locked));
      setSchemaLockedAt(res.data_provider.schema_locked_at ?? null);
      setSavedSnapshot(
        JSON.stringify({
          name: name.trim(),
          description: description.trim() || null,
          sql: sql.trim(),
          connector_id: connectorId || null,
          parameters
        })
      );
      if (!opts?.quiet) toast.push({ kind: "success", message: "Saved" });
      return true;
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
      return false;
    } finally {
      setBusy(false);
    }
  }

  function handleFieldBlur(e: React.FocusEvent) {
    if (e.relatedTarget instanceof HTMLButtonElement) return;
    if (dirty && canSave && !busy) void save({ quiet: true });
  }

  /** ADR-0146: freeze / unfreeze this provider's output shape. */
  async function toggleSchemaLock() {
    setBusy(true);
    try {
      const res = await apiFetch<{ data_provider: DataProvider }>(
        `/api/tenant/data-providers/${encodeURIComponent(providerId)}`,
        { method: "PATCH", body: { schema_locked: !schemaLocked } }
      );
      setSchemaLocked(Boolean(res.data_provider.schema_locked));
      setSchemaLockedAt(res.data_provider.schema_locked_at ?? null);
      toast.push({
        kind: "success",
        message: schemaLocked ? "Schema unlocked." : "Schema locked."
      });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  async function toggleLock() {
    setBusy(true);
    try {
      const res = await apiFetch<{ data_provider: DataProvider }>(
        `/api/tenant/data-providers/${encodeURIComponent(providerId)}`,
        {
          method: "PATCH",
          body: { source_sql_locked: !sourceSqlLocked }
        }
      );
      setSourceSqlLocked(Boolean(res.data_provider.source_sql_locked));
      toast.push({
        kind: "success",
        message: sourceSqlLocked ? "SQL unlocked." : "SQL locked for production."
      });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  async function copySql() {
    if (!sql.trim()) return;
    try {
      await navigator.clipboard.writeText(sql);
      toast.push({ kind: "success", message: "SQL copied" });
    } catch {
      toast.push({ kind: "error", message: "Could not copy to clipboard" });
    }
  }

  async function execute() {
    if (!sql.trim()) return;
    setBusy(true);
    setPreviewError(null);
    try {
      // ADR-0120 §5.7: the execute response is a RECEIPT — re-fetch the
      // detail route for the (preview-coalesced, bounded) grid + schema data.
      const result = await apiFetch<{
        ok: boolean;
        rowCount: number;
        durationMs: number;
        runAt: string;
      }>(`/api/tenant/data-providers/${encodeURIComponent(providerId)}/execute`, {
        method: "POST",
        body: sourceSqlLocked ? {} : { source_spec: { specVersion: "source.v1", kind: "sql", sql: sql.trim() } }
      });
      setSavedSnapshot(
        JSON.stringify({
          name: name.trim(),
          description: description.trim() || null,
          sql: sql.trim(),
          connector_id: connectorId || null,
          parameters
        })
      );
      const refreshed = await apiFetch<{ data_provider: DataProvider }>(
        `/api/tenant/data-providers/${encodeURIComponent(providerId)}`
      );
      setCache({
        sourceResult: refreshed.data_provider.last_source_result,
        runAt: result.runAt,
        durationMs: result.durationMs,
        rowCount: result.rowCount
      });
      await loadExecutionHistory();
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : String(e));
      await loadExecutionHistory();
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <LoadingState />;
  if (error) return <ErrorState detail={error} />;

  return (
    <PageLayout
      title={
        <span className="breadcrumbTitle">
          <button className="breadcrumbLink" type="button" onClick={() => nav("/app/build/providers")}>
            Data Providers
          </button>
          <span className="breadcrumbSep">/</span>
          <span>{name || "Data Provider"}</span>
        </span>
      }
      description={description || undefined}
      actions={
        <>
          <button className="btn btnPrimary" type="button" onClick={() => void save()} disabled={busy || !dirty || name.trim() === "" || sql.trim() === "" || !connectorId}>
            Save
          </button>
        </>
      }
    >
      <div className="reportEditorGrid">
        <section className="card reportEditorLeft">
          <div className="reportTabs" role="tablist" aria-label="Data provider editor sections">
            <button className={`reportTabBtn${activeTab === "metadata" ? " reportTabBtnActive" : ""}`} type="button" onClick={() => setActiveTab("metadata")}>
              Metadata
            </button>
            <button className={`reportTabBtn${activeTab === "source" ? " reportTabBtnActive" : ""}`} type="button" onClick={() => setActiveTab("source")}>
              SQL
            </button>
            <button className={`reportTabBtn${activeTab === "parameters" ? " reportTabBtnActive" : ""}`} type="button" onClick={() => setActiveTab("parameters")}>
              Parameters
            </button>
            <button className={`reportTabBtn${activeTab === "schema" ? " reportTabBtnActive" : ""}`} type="button" onClick={() => setActiveTab("schema")} disabled={!cache?.sourceResult}>
              Schema Contract
            </button>
            <button className={`reportTabBtn${activeTab === "history" ? " reportTabBtnActive" : ""}`} type="button" onClick={() => setActiveTab("history")}>
              Execution History
            </button>
          </div>

          {activeTab === "metadata" ? (
            <div className="reportSection">
              <h3 className="reportSectionTitle">Metadata</h3>
              <div className="field">
                <label className="label" htmlFor="dp-name">
                  Name
                </label>
                <input id="dp-name" className="input" value={name} onChange={(e) => setName(e.target.value)} onBlur={handleFieldBlur} />
              </div>
              <div className="field">
                <label className="label" htmlFor="dp-description">
                  Description
                </label>
                <textarea id="dp-description" className="input reportTextarea" value={description} onChange={(e) => setDescription(e.target.value)} onBlur={handleFieldBlur} />
              </div>
              <div className="field">
                <label className="label" htmlFor="dp-connector">
                  Connector
                </label>
                <select id="dp-connector" className="input" value={connectorId} onChange={(e) => setConnectorId(e.target.value)} onBlur={handleFieldBlur}>
                  <option value="" disabled>
                    Select connector
                  </option>
                  {connectors.map((connector) => (
                    <option key={connector.id} value={connector.id}>
                      {connector.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label
                  className="label"
                  htmlFor="dp-sql-lock"
                  style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 0 }}
                >
                  <input
                    id="dp-sql-lock"
                    type="checkbox"
                    checked={sourceSqlLocked}
                    disabled={busy || (!sourceSqlLocked && sqlDirty)}
                    onChange={() => void toggleLock()}
                  />
                  Production SQL lock
                </label>
              </div>
              {/* ADR-0146 — sibling of the SQL lock, deliberately independent:
                  that one freezes the QUERY, this one freezes the SHAPE. Never
                  disabled by edit state — a provider that has never run locks
                  now and freezes on its next landing. */}
              <div className="field">
                <label
                  className="label"
                  htmlFor="dp-schema-lock"
                  style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 0 }}
                >
                  <input
                    id="dp-schema-lock"
                    type="checkbox"
                    checked={schemaLocked}
                    disabled={busy}
                    onChange={() => void toggleSchemaLock()}
                  />
                  Schema lock
                  {schemaLocked ? (
                    <span
                      className="pill"
                      title={
                        schemaLockedAt
                          ? `Schema locked ${new Date(schemaLockedAt).toLocaleString()} — output shape is frozen. A run that changes the shape is flagged for validation in every operation using this provider. Unlock to re-freeze.`
                          : "Output shape is frozen."
                      }
                    >
                      Schema locked
                    </span>
                  ) : null}
                </label>
                <div className="help">
                  Freezes this provider&apos;s output shape. Operations using it are flagged if a run changes the columns.
                </div>
              </div>
            </div>
          ) : null}

          {activeTab === "source" ? (
            <div className="reportSection">
              <div className="reportSectionHeader">
                <h3 className="reportSectionTitle">{sourceSqlLocked ? "SQL (locked for production)" : "SQL"}</h3>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button className="btn btnSm" type="button" onClick={() => void copySql()} disabled={!sql.trim()}>
                    Copy SQL
                  </button>
                  <button className="btn btnSm" type="button" onClick={() => void execute()} disabled={busy || !sql.trim()}>
                    Execute
                  </button>
                </div>
              </div>
              <div className="queryEditorCm">
                <CodeMirror
                  value={sql}
                  height="240px"
                  extensions={sqlExtensions}
                  placeholder="SELECT ..."
                  readOnly={sourceSqlLocked}
                  editable={!sourceSqlLocked}
                  onChange={(val) => setSql(val)}
                  onBlur={handleFieldBlur}
                  basicSetup={{
                    lineNumbers: true,
                    foldGutter: false,
                    highlightActiveLine: true,
                    bracketMatching: true,
                    autocompletion: false,
                  }}
                />
              </div>
            </div>
          ) : null}

          {activeTab === "parameters" ? (
            <div className="reportSection">
              <div className="reportSectionHeader">
                <h3 className="reportSectionTitle">Parameters</h3>
                <button
                  className="btn btnSm"
                  type="button"
                  onClick={() =>
                    setParameters((prev) => [
                      ...prev,
                      { name: `param_${prev.length + 1}`, type: "string", required: false, default: "", description: "" }
                    ])
                  }
                >
                  Add parameter
                </button>
              </div>
              {parameters.length === 0 ? <div className="muted">No parameters</div> : null}
              {parameters.length > 0 ? (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Required</th>
                      <th>Default</th>
                      <th>Description</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {parameters.map((param, idx) => (
                      <tr key={`${param.name}-${idx}`} onBlur={handleFieldBlur}>
                        <td>
                          <input
                            className="input"
                            value={param.name}
                            onChange={(e) =>
                              setParameters((prev) => {
                                const next = [...prev];
                                next[idx] = { ...next[idx], name: e.target.value };
                                return next;
                              })
                            }
                          />
                        </td>
                        <td>
                          <select
                            className="input"
                            value={param.type}
                            onChange={(e) =>
                              setParameters((prev) => {
                                const next = [...prev];
                                next[idx] = { ...next[idx], type: e.target.value as DataProvider["parameters"][number]["type"] };
                                return next;
                              })
                            }
                          >
                            <option value="string">string</option>
                            <option value="int">int</option>
                            <option value="decimal">decimal</option>
                            <option value="bool">bool</option>
                            <option value="date">date</option>
                            <option value="datetime">datetime</option>
                          </select>
                        </td>
                        <td>
                          <input
                            type="checkbox"
                            checked={param.required}
                            onChange={(e) =>
                              setParameters((prev) => {
                                const next = [...prev];
                                next[idx] = { ...next[idx], required: e.target.checked };
                                return next;
                              })
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="input"
                            value={param.default == null ? "" : String(param.default)}
                            onChange={(e) =>
                              setParameters((prev) => {
                                const next = [...prev];
                                next[idx] = {
                                  ...next[idx],
                                  default:
                                    e.target.value === ""
                                      ? null
                                      : next[idx].type === "int" || next[idx].type === "decimal"
                                      ? Number(e.target.value)
                                      : next[idx].type === "bool"
                                      ? e.target.value === "true"
                                      : e.target.value
                                };
                                return next;
                              })
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="input"
                            value={param.description ?? ""}
                            onChange={(e) =>
                              setParameters((prev) => {
                                const next = [...prev];
                                next[idx] = { ...next[idx], description: e.target.value || null };
                                return next;
                              })
                            }
                          />
                        </td>
                        <td>
                          <button
                            className="btn btnDanger btnSm"
                            type="button"
                            onClick={() =>
                              setParameters((prev) => {
                                const next = [...prev];
                                next.splice(idx, 1);
                                return next;
                              })
                            }
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
            </div>
          ) : null}

          {activeTab === "schema" ? (
            <div className="reportSection">
              <h3 className="reportSectionTitle">Schema Contract</h3>
              {cache?.sourceResult ? (
                <>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Column Name</th>
                        <th>Type</th>
                        <th>Domain</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cache.sourceResult.columns.map((column) => (
                        <tr key={column.name}>
                          <td>{column.name}</td>
                          <td>{column.rawType ?? column.type}</td>
                          <td>
                            <ColumnDomainSelect
                              columnName={column.name}
                              resolvedDomain={column.type}
                              override={columnOverrides[column.name]}
                              onChange={changeColumnDomain}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : (
                <div className="muted">No schema available. Execute SQL to generate schema contract.</div>
              )}
            </div>
          ) : null}
          {activeTab === "history" ? (
            <div className="reportSection">
              <h3 className="reportSectionTitle">Execution History</h3>
              {executions.length === 0 ? (
                <div className="muted">No executions yet.</div>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Status</th>
                      <th>Rows</th>
                      <th>Duration</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {executions.map((run) => (
                      <tr key={run.id} style={{ cursor: "pointer" }} onClick={() => setSelectedExecution(run)}>
                        <td>{new Date(run.finished_at).toLocaleString()}</td>
                        <td>
                          <span style={{ color: run.status === "failed" ? "var(--danger-700, #dc2626)" : "var(--success-700, #16a34a)", fontWeight: 600 }}>
                            {run.status}
                          </span>
                        </td>
                        <td>{run.row_count != null ? run.row_count.toLocaleString() : "–"}</td>
                        <td>{run.duration_ms} ms</td>
                        <td style={{ color: run.error ? "var(--danger-700, #dc2626)" : undefined }}>
                          {run.error ? run.error.slice(0, 80) + (run.error.length > 80 ? "…" : "") : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : null}
        </section>

        {activeTab === "source" ? (
          <section className="card reportEditorPreview">
            <h3 className="reportSectionTitle">Results (Cache)</h3>
            {previewError ? <ErrorState detail={previewError} /> : null}
            {cache?.sourceResult ? (
              <>
                <div className="queryMeta">
                  {cache.runAt ? <span className="pill">Last run: {new Date(cache.runAt).toLocaleString()}</span> : null}
                  {cache.durationMs != null ? <span className="pill">Duration: {cache.durationMs} ms</span> : null}
                  {cache.rowCount != null ? <span className="pill">Rows: {cache.rowCount.toLocaleString()}</span> : null}
                </div>
                {cacheTanModel ? <TanTableViz model={cacheTanModel} /> : null}
              </>
            ) : (
              <div className="muted">No cache yet. Click Execute to run the SQL and cache results.</div>
            )}
          </section>
        ) : null}
      </div>

      {selectedExecution ? (() => {
        const details = (selectedExecution.details ?? {}) as Record<string, unknown>;
        const resolvedSql = typeof details.resolved_sql === "string" ? details.resolved_sql : null;
        return (
          <div className="modalOverlay" role="presentation" onClick={() => setSelectedExecution(null)}>
            <div className="modal modalScrollable" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760 }}>
              <div className="modalScrollHeader reportModalTitle">
                Execution details — {new Date(selectedExecution.finished_at).toLocaleString()}
              </div>
              <div className="modalScrollBody">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12, marginTop: 12 }}>
                  <div className="field">
                    <label className="label">Status</label>
                    <div style={{ color: selectedExecution.status === "failed" ? "var(--danger-700, #dc2626)" : "var(--success-700, #16a34a)", fontWeight: 600 }}>
                      {selectedExecution.status}
                    </div>
                  </div>
                  <div className="field">
                    <label className="label">Duration</label>
                    <div>{selectedExecution.duration_ms} ms</div>
                  </div>
                  <div className="field">
                    <label className="label">Rows</label>
                    <div>{selectedExecution.row_count ?? "–"}</div>
                  </div>
                </div>
                {selectedExecution.error ? (
                  <div className="field" style={{ marginBottom: 12 }}>
                    <label className="label">Error</label>
                    <div style={{ color: "var(--danger-700, #dc2626)", whiteSpace: "pre-wrap" }}>{selectedExecution.error}</div>
                  </div>
                ) : null}
                {resolvedSql ? (
                  <div className="field" style={{ marginBottom: 12 }}>
                    <label className="label">SQL</label>
                    <pre style={{ margin: 0, padding: 10, background: "var(--surface, #f8fafc)", border: "1px solid var(--border)", borderRadius: 6, whiteSpace: "pre-wrap", fontSize: 12, lineHeight: 1.5, fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" }}>
                      {resolvedSql}
                    </pre>
                  </div>
                ) : null}
              </div>
              <div className="modalScrollFooter actions" style={{ marginTop: 16 }}>
                <button className="btn" type="button" onClick={() => setSelectedExecution(null)}>Close</button>
              </div>
            </div>
          </div>
        );
      })() : null}
    </PageLayout>
  );
}
